import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ExerciseCard = ({ 
  exercise, 
  onEdit, 
  onView, 
  onToggleStatus, 
  onDelete 
}) => {
  const [imageError, setImageError] = useState(false);

  const getDifficultyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return 'text-success bg-success/10';
      case 'intermediate': return 'text-warning bg-warning/10';
      case 'advanced': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      'strength': 'Dumbbell',
      'cardio': 'Heart',
      'flexibility': 'Zap',
      'balance': 'Target',
      'rehabilitation': 'Shield',
      'mobility': 'RotateCcw'
    };
    return categoryIcons?.[category?.toLowerCase()] || 'Activity';
  };

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group">
      {/* Exercise Image */}
      <div className="relative h-48 bg-muted overflow-hidden">
        {exercise?.image && !imageError ? (
          <Image
            src={exercise?.image}
            alt={exercise?.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <Icon 
              name={getCategoryIcon(exercise?.category)} 
              size={48} 
              className="text-muted-foreground"
            />
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${exercise?.isActive 
              ? 'bg-success text-success-foreground' 
              : 'bg-muted text-muted-foreground'
            }
          `}>
            {exercise?.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Video Indicator */}
        {exercise?.hasVideo && (
          <div className="absolute top-3 left-3">
            <div className="bg-black/70 text-white px-2 py-1 rounded-full flex items-center space-x-1">
              <Icon name="Play" size={12} />
              <span className="text-xs">Video</span>
            </div>
          </div>
        )}

        {/* Quick Actions Overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onView(exercise)}
            className="breathing-focus"
          >
            <Icon name="Eye" size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(exercise)}
            className="breathing-focus bg-surface"
          >
            <Icon name="Edit" size={16} />
          </Button>
        </div>
      </div>
      {/* Exercise Details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground text-lg leading-tight">
            {exercise?.name}
          </h3>
          <div className="flex items-center space-x-1 ml-2">
            <Icon name={getCategoryIcon(exercise?.category)} size={16} className="text-muted-foreground" />
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {exercise?.description || 'No description available'}
        </p>

        {/* Category and Type */}
        <div className="flex items-center space-x-2 mb-3">
          <span className="px-2 py-1 bg-accent text-accent-foreground rounded-md text-xs font-medium">
            {exercise?.category}
          </span>
          <span className="px-2 py-1 bg-muted text-muted-foreground rounded-md text-xs">
            {exercise?.type === 'dynamic' ? 'Dynamic' : 'Static'}
          </span>
        </div>

        {/* Difficulty Levels */}
        <div className="space-y-2 mb-4">
          <p className="text-xs font-medium text-muted-foreground">Difficulty Levels:</p>
          <div className="grid grid-cols-3 gap-2">
            {exercise?.difficultyLevels?.map((level, index) => (
              <div key={index} className="text-center">
                <div className={`
                  px-2 py-1 rounded-md text-xs font-medium
                  ${getDifficultyColor(level?.phase)}
                `}>
                  {level?.reps} reps
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {level?.phase}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(exercise)}
              className="breathing-focus"
            >
              <Icon name="Edit" size={16} className="mr-1" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleStatus(exercise)}
              className="breathing-focus"
            >
              <Icon name={exercise?.isActive ? 'EyeOff' : 'Eye'} size={16} className="mr-1" />
              {exercise?.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(exercise)}
            className="breathing-focus text-error hover:text-error"
          >
            <Icon name="Trash2" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExerciseCard;